import PathManager from 'uri-path-manager'

import Config from '@/app.config'
import Logger from '@/logger'
import ServiceFactory from '@/services/service-factory'

import Store from '@/store'
import { ACTIONS } from '@/store/actions'

import LinkedSchemeData from '@/models/linked-scheme-data'
import PartnerSchemes from '@/schemes/partner-schemes'
import Property from '@/models/property'
import PropertyInfo from '@/models/property-info'

const loggingCategory = 'Scheme Servicing'

/*
 * The Scheme Servicing Manager deals with interactions with the schemes service and provides aids to determine what scheme services a given situation.
 */
const SchemeServicingManager = {
    /**
     * @type {?Scheme}
     * @private
     */
    _scheme: null,

    /**
     * Create a new scheme and return it.
     * @returns {Scheme}
     */
    async createSchemeAsync() {
        if (this._scheme !== null) return this._scheme

        const schemeMap = this.getSchemeMap()
        const theme = schemeMap ? schemeMap.theme : 'none'

        const { default: Scheme } = await import(
            /* webpackChunkName: "scheme-[request]" */
            '@/schemes/' + theme
        )

        this._scheme = await new Scheme()
            .loadResourcesAsync()

        if (schemeMap.currentHost)
            this._scheme.setSchemeHost(schemeMap.currentHost)

        return this._scheme
    },

    /**
     * Returns the scheme instance used as a Vue plugin.
     * @returns {Scheme}
     */
    getActiveScheme() {
        return this._scheme
    },

    /**
     * Returns the linked scheme data for the current project address, either from Vuex or, if not available,
     * by calling the API and updating Vuex along the way.
     * @async
     * @return {Promise<LinkedSchemeData>} The linked scheme data.
     */
    async ensureLinkedSchemeDataAsync() {
        let retVal

        if (Store.state.propertyLinkedSchemes) {
            retVal = LinkedSchemeData.ensure(Store.state.propertyLinkedSchemes)
            Logger.logObject(retVal, 'Linked Scheme data loaded from Vuex', loggingCategory)
        } else {
            try {
                retVal = await this.loadLinkedSchemeDataAsync()
                Logger.logObject(retVal, 'Linked Scheme data loaded from API', loggingCategory)
            } catch (err) {
                throw err
            }
        }

        return retVal
    },

    /**
     * Update the services provided by the scheme based on the partner referral and cache it in Vuex.
     * @async
     * @param {?number} partnerReferralId
     */
    async updatePartnerServicesAsync(partnerReferralId) {
        await Store.dispatch(ACTIONS.updatePartnerReferralId, partnerReferralId)
        const partner = Config.partnerLinks.find(pl => pl.id === partnerReferralId)

        if (partner) {
            this._scheme.partnerLink = partner
            Logger.logObject(this._scheme.services, 'Updating services', loggingCategory)
        } else {
            this._scheme.partnerLink = null
        }
    },

    /**
     * Returns the default servicedSchemeId to use based on the address and current scheme information.
     * @async
     * @return {Promise<?Scheme>} The serviced scheme.
     */
    async getDefaultServicedSchemeAsync() {
        try {
            const presentationScheme = await this.getPresentationSchemeAsync()
            if (presentationScheme.isUmbrella) {
                const relevantLinkedSchemes = await this.ensureLinkedSchemeDataAsync()
                if (relevantLinkedSchemes && relevantLinkedSchemes.defaultSchemeId)
                    return await this.getSchemeAsync(relevantLinkedSchemes.defaultSchemeId)
            } else {
                return presentationScheme.userOnlyTool ? null : presentationScheme
            }
        } catch (err) {
            throw err
        }

        return null
    },

    /**
     * Returns all registered presentation schemes in the app.
     *
     * Schemes marked as `isUmbrella` or in the default list ('parity', '(none)') are excluded.
     *
     * The definition rather than the index must be imported to prevent re-importing CSS dependencies.
     * @async
     * @param {boolean} servicedOnly Indicates if the method should just return serviced schemes.
     * @return {Promise<Array<Scheme>>} The presentation schemes
     */
    async getAllPresentationSchemesAsync(servicedOnly = false) {
        const promisedSchemes = Config.schemes
            .map(async(s) => await this._getSchemeObjectAsync(s.theme))

        return (await Promise.all(promisedSchemes))
            .filter(s => ![ 'none' ].includes(s.path))
            .filter(s => !s.isUmbrella)
            .filter(s => !servicedOnly || servicedOnly && !s.userOnlyTool)
    },

    /**
     * Returns all the partner scheme names for a national scheme.
     * @returns {Promise<Array<PartnerSchemes>>}
     */
    async getPartnersForUmbrellaSchemeAsync() {
        let partnerSchemeIds = []

        try {
            const schemeService = ServiceFactory.scheme()
            const schemeOperationState = await schemeService.loadServicedSchemesAsync()
            const partnerSchemes = schemeOperationState.data

            partnerSchemeIds = partnerSchemes.map(({ id }) => id)
        } catch (err) {
            Logger.error(err, loggingCategory)
        }

        const schemes = await this.getAllPresentationSchemesAsync()
        const partnerNameMap = {}

        /**
         * @param {number} partnerId
         * @param {string} partnerName
         * @param {Scheme} scheme
         */
        function addPartnerSchemeToPartnerNameMap(partnerId, partnerName, scheme) {
            const existingPartner = partnerNameMap[partnerName]

            if (existingPartner)
                existingPartner.linkedSchemes.push(scheme)
            else
                partnerNameMap[partnerName] = new PartnerSchemes(partnerId, partnerName, scheme)
        }

        for (let i = 0; i < schemes.length; i++) {
            const scheme = schemes[i]
            const partnerName = scheme.policyData.licensee.name
            addPartnerSchemeToPartnerNameMap(scheme.id, partnerName, scheme)

            if (partnerName !== Config.defaultPartnerGroup.name && scheme.policyData.operator.isRetrofitWorks)
                addPartnerSchemeToPartnerNameMap(Config.defaultPartnerGroup.id, Config.defaultPartnerGroup.name, scheme)
        }

        // convert the hash map to an array of values and filter out anything that isn't in the original API call
        return Object.values(partnerNameMap).filter(({ id }) => partnerSchemeIds.includes(id))
    },

    /**
     * Returns the current presentation scheme in use, based on the URI host.
     * @async
     * @return {Promise<Scheme>} The presentation scheme
     */
    async getPresentationSchemeAsync() {
        const schemeMap = this.getSchemeMap()
        return schemeMap
            ? await this._getSchemeObjectAsync(schemeMap.theme)
            : null
    },

    /**
     * Returns the scheme with the specified identifier.
     * The definition rather than the index must be imported to prevent re-importing CSS dependencies.
     * @async
     * @param {number} schemeId A scheme identifier to load a specific scheme.
     * @return {Promise<Scheme>} The scheme
     */
    async getSchemeAsync(schemeId) {
        const schemeMap = this.getSchemeMap(schemeId)
        if (!schemeMap) return null

        const { default: Scheme } = await import(
            /* webpackChunkName: "scheme-[request]" */
            '@/schemes/' + schemeMap.theme + '/definition'
        )

        return new Scheme()
    },

    /**
     * Returns the host map data for the current scheme in use, based on the URI host.
     *
     * @param {?number} schemeId An optional identifier to load a specific presentation scheme, regardless of the host.
     * @return {Object} The scheme map data from app.config.
     */
    getSchemeMap(schemeId = null) {
        let schemeMap

        if (schemeId) {
            schemeMap = Config.schemes.find(item => item.id === schemeId)
        } else {
            const hostname = new PathManager().host()
            const findScheme = searchHost => Config.schemes.find(({ schemeHost }) => schemeHost.map(({ host }) => host).indexOf(searchHost) > -1)

            schemeMap = findScheme(hostname)
            schemeMap = schemeMap || findScheme('DEFAULT')

            const allHosts = Config.schemes.flatMap(({ schemeHost }) => schemeHost)

            schemeMap.currentHost = allHosts.find(({ host }) => host === hostname) || allHosts.find(({ host }) => host === 'DEFAULT')
        }

        return schemeMap || null
    },

    /**
     * Loads linked scheme data from the services.
     *
     * @async
     * @param {Property} property An optional property. If not provided, will be loaded from state. If not there this will do nothing and return null.
     * @returns {Promise<?LinkedSchemeData>} The scheme data linked to the provided property.
     * @throws {TypeError} property is null.
     */
    async loadLinkedSchemeDataAsync(property = null) {
        if (!property)
            property = Store.state.property !== null ? Property.ensure(Store.state.property) : null

        if (!property) return null

        let schemeOperationState, schemeInfo

        try {
            const schemeService = ServiceFactory.scheme()
            schemeOperationState = await schemeService.loadServicedSchemesForAddressAsync(property.id)
            schemeInfo = schemeOperationState.data
        } catch (err) {
            Logger.error(err)
            schemeInfo = null
        }

        if (schemeOperationState && schemeOperationState.succeeded) {
            schemeInfo = schemeInfo
                ? LinkedSchemeData.ensure(schemeInfo)
                : null
            await Store.dispatch(ACTIONS.updateChosenProperty, new PropertyInfo(property, schemeInfo))
            return schemeInfo
        }

        await Store.dispatch(ACTIONS.updateChosenProperty, new PropertyInfo(property, null))
        return null
    },

    /**
     * Returns the presentation scheme with the provided theme key.
     *
     * The definition rather than the index must be imported to prevent re-importing CSS dependencies.
     * @param {string} forTheme The theme key, used to find the scheme data.
     * @returns {Promise<Scheme>} The scheme object itself.
     * @private
     */
    async _getSchemeObjectAsync(forTheme) {
        const { default: Scheme } = await import(
            /* webpackChunkName: "scheme-[request]" */
        '@/schemes/' + forTheme + '/definition'
            )

        return new Scheme()
    },
}

export default SchemeServicingManager
